import moment from "moment";
import {
  formatTime,
  formatWrittenDay,
  formatWrittenDayMonth,
  formatWrittenDayMonthYear,
  formatWrittenDayMonthYearShort,
  type TimeDifferenceStrings
} from "./momentutils";
import { getRealStartEndDate, getShowTimeDifferenceLabel } from "./showDateUtils";

// The localized text fragments that the date text and night text need.
// Because localization works differently on mobile & front-end.
// The duration words are inherited from TimeDifferenceStrings.
export interface ShowDateTimeStrings extends TimeDifferenceStrings {
  // en-US: "until and inc."
  untilAndIncludingAbbreviated: string;
  // en-US: "In the night of {0} - {1}"
  inTheNightOfXY: string;
}

// Describes which clock time to show and how to emphasize it.
export type ShowTimeDisplay =
  // Nothing to render. This happens for an all day appointment that has no fallback time.
  | { kind: "none" }
  // Free text, can contain anything
  | { kind: "fallback"; text: string }
  // One single time, e.g. "09:00".
  | { kind: "single"; start: string }
  // A start & end time. 
  // When deEmphasizeEnd is true the end falls on a later day, the component greys it out
  | { kind: "range"; start: string; end: string; deEmphasizeEnd: boolean };

// The text pieces that describe the appointment date. The component shows each
// piece on its own row and renders the clock times below them.
export interface ShowDateTimeDisplay {
  // Same day example: "Fri, 1 March 2024 (1 hour)".
  // Spanning days example: "Fri, 1 Mar until and inc. Sat, 2 Mar 2024 (1 day, 1 hour)".
  dateText: string;
  // Shown only for appointments that start in the night, empty otherwise. 
  //"In the night of" followed by the evening date and the next morning date.
  inTheNightOfText: string;
}

export const getShowTime = (dateTime: server.dto.AppointmentDateTime): ShowTimeDisplay => {
  if (dateTime.mode === "allDay") {
    if (!dateTime.customFallbackTime) {
      return { kind: "none" };
    }

    return { kind: "fallback", text: dateTime.customFallbackTime };
  }

  const start = moment.utc(dateTime.startDisplay);
  const end = moment.utc(dateTime.endDisplay);

  if (start.isSame(end)) {
    return { kind: "single", start: formatTime(start) };
  }

  const deEmphasizeEnd = !start.isSame(end, "day");
  return { kind: "range", start: formatTime(start), end: formatTime(end), deEmphasizeEnd };
};

// Builds the date text and the optional night text. The branching mirrors how an
// appointment is shown across the different modes and across single or multiple
// day spans. Localized fragments arrive through strings.
export const getShowDateTimeDisplay = (
  dateTime: server.dto.AppointmentDateTime,
  strings: ShowDateTimeStrings
): ShowDateTimeDisplay => {
  const start = moment.utc(dateTime.startDisplay);
  const end = moment.utc(dateTime.endDisplay);
  const isSameDay = start.isSame(end, "day");

  const timeDifferenceLabel = getShowTimeDifferenceLabel({
    startDisplayDate: start,
    endDisplayDate: end,
    timeOfPerformance: dateTime.customFallbackTime,
    mode: dateTime.mode
  }, strings);

  const label = timeDifferenceLabel ? `(${timeDifferenceLabel})` : "";

  const dateText = isSameDay
    ? buildSameDayText(dateTime.mode, start, label)
    : buildMultiDayText(start, end, strings.untilAndIncludingAbbreviated, label);

  const inTheNightOfText = dateTime.mode === "hasTimeStartsInNight"
    ? buildNightText(dateTime, start, strings.inTheNightOfXY)
    : "";

  return { dateText, inTheNightOfText };
};

// Joins the parts that are not empty with a single space.
const joinTextParts = (parts: string[]): string => parts.filter(part => part).join(" ");

const buildSameDayText = (mode: server.dto.AppointmentTimeModes, start: moment.Moment, label: string): string => {
  // An all day appointment on a single day never shows the duration label.
  if (mode === "allDay") {
    return formatWrittenDayMonthYear(start);
  }

  return joinTextParts([formatWrittenDayMonthYear(start), label]);
};

const buildMultiDayText = (start: moment.Moment, end: moment.Moment, untilAndIncluding: string, label: string): string => {
  return joinTextParts([
    formatWrittenDayMonth(start),
    untilAndIncluding,
    formatWrittenDayMonthYearShort(end),
    label
  ]);
};

const buildNightText = (dateTime: server.dto.AppointmentDateTime, start: moment.Moment, template: string): string => {
  const { startRealDate } = getRealStartEndDate({
    startDisplayDate: moment.utc(dateTime.startDisplay),
    endDisplayDate: moment.utc(dateTime.endDisplay),
    timeOfPerformance: dateTime.customFallbackTime,
    mode: dateTime.mode
  });

  const realStartAndDisplayIsSameMonth = startRealDate.isSame(start, "month");
  const eveningLabel = realStartAndDisplayIsSameMonth
    ? formatWrittenDay(start)
    : formatWrittenDayMonth(start);
  const morningLabel = formatWrittenDayMonthYearShort(startRealDate);

  return template.replace("{0}", eveningLabel).replace("{1}", morningLabel);
};
